import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT } from '../../../../utils/defaultDateFormats';
import type {
  DataGridChecklist,
  DataGridChecklistTreeRow,
  SchedulerChecklist,
} from '../types';

export const DAY_GROUP_TEXT_FORMAT = `dddd - ${DEFAULT_DATE_FORMAT}`;

// Prefix used to identify synthetic day-group rows in the tree-data grid.
// Kept stable so isCheckboxesEnabled and CSS selectors keep matching.
export const DAY_GROUP_ROW_ID_PREFIX = 'auto-generated-row-day/';

export const checklistToSchedulesDataGridRow = (
  checklist: SchedulerChecklist
): DataGridChecklist => {
  return {
    ...checklist,
    id: checklist.externalId,
    day: checklist.startTime
      ? dayjs(checklist.startTime).format(DAY_GROUP_TEXT_FORMAT)
      : undefined,
    createdByExternalId: checklist.createdBy?.externalId, // Extract externalId as string for easier filtering operations
    nextOccurrence: checklist.nextOccurrence,
    solutionTags: checklist.solutionTags,
  };
};

/**
 * Convert a sorted list of DataGridChecklist rows into a tree-data flat list
 * suitable for `treeDataKey='hierarchy'` on the Cogs Lab DataGrid (MUI Pro).
 *
 * For each unique day, a synthetic group row is emitted before its checklist
 * rows so the grid renders a collapsible day header. Rows without a `day`
 * (missing startTime) become root-level leaves, mirroring the old row-grouping
 * behaviour where the null group was always visible.
 */
export const buildSchedulesTreeDataRows = (
  rows: DataGridChecklist[]
): DataGridChecklistTreeRow[] => {
  const result: DataGridChecklistTreeRow[] = [];
  const groupIndexByDay = new Map<string, number>();

  for (const row of rows) {
    if (row.day === undefined) {
      result.push({ ...row, hierarchy: [row.id] });
      continue;
    }

    let groupIndex = groupIndexByDay.get(row.day);
    if (groupIndex === undefined) {
      groupIndex = result.length;
      groupIndexByDay.set(row.day, groupIndex);
      result.push({
        id: `${DAY_GROUP_ROW_ID_PREFIX}${row.day}`,
        externalId: `${DAY_GROUP_ROW_ID_PREFIX}${row.day}`,
        day: row.day,
        hierarchy: [row.day],
        isDayGroup: true,
        groupSize: 0,
      });
    }

    const group = result[groupIndex];
    if (group.isDayGroup) {
      group.groupSize += 1;
    }
    result.push({ ...row, hierarchy: [row.day, row.id] });
  }

  return result;
};

/**
 * Utility type to extract keys from T whose values are string or undefined.
 * This ensures that the 'key' parameter passed to the sort functions refers
 * to a property that is suitable for localeCompare.
 *
 * @template T The type of the object.
 */
export type SortableStringKeys<T> = {
  [K in keyof T]: T[K] extends string | undefined ? K : never;
}[keyof T];

/**
 * Sorts an array of DataGridChecklist objects by a specified string property
 * in a locale-sensitive manner. Handles cases where the property might be
 * missing (undefined) on some objects, consistently placing them
 * before valid string values.
 *
 * @param {Array<DataGridChecklist>} data The array of objects to sort.
 * @param {SortableStringKeys<DataGridChecklist>} key The name of the string property to sort by (e.g., 'startTime', 'title').
 * @returns {Array<DataGridChecklist>} A new array with the sorted objects.
 */
export const sortByStringProperty = (
  data: DataGridChecklist[],
  key: SortableStringKeys<DataGridChecklist>
): DataGridChecklist[] => {
  const sortedData: DataGridChecklist[] = [...data];
  sortedData.sort((a: DataGridChecklist, b: DataGridChecklist) => {
    const valueA = a[key as keyof DataGridChecklist];
    const valueB = b[key as keyof DataGridChecklist];

    // Case 1: Both are strings
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return valueA.localeCompare(valueB);
    }
    // Case 2: Ensure undefined values are sorted before strings
    else if (typeof valueA === 'string' && valueB === undefined) {
      return 1;
    } else if (valueA === undefined && typeof valueB === 'string') {
      return -1;
    }
    // Case 3: Both are undefined, maintain relative order
    else {
      return 0;
    }
  });
  return sortedData;
};

/**
 * Sorts an array of DataGridChecklist objects by multiple string properties
 * in a locale-sensitive manner. The sort order is determined by the order of keys provided.
 * Handles cases where properties might be missing (undefined), consistently
 * placing them before valid string values for each comparison level.
 *
 * @param {Array<DataGridChecklist>} data The array of objects to sort.
 * @param {(SortableStringKeys<DataGridChecklist>)[]} keys An array of string property names to sort by, in order of precedence.
 * @returns {Array<DataGridChecklist>} A new array with the sorted objects.
 */
export const sortByMultipleStringProperties = (
  data: DataGridChecklist[],
  keys: SortableStringKeys<DataGridChecklist>[]
): DataGridChecklist[] => {
  const sortedData: DataGridChecklist[] = [...data];
  sortedData.sort((a: DataGridChecklist, b: DataGridChecklist) => {
    for (const key of keys) {
      const valueA = a[key as keyof DataGridChecklist];
      const valueB = b[key as keyof DataGridChecklist];

      // Case 1: Both are strings
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        const comparison = valueA.localeCompare(valueB);
        if (comparison !== 0) {
          return comparison;
        }
      }
      // Case 2: Ensure undefined values are sorted before strings
      else if (typeof valueA === 'string' && valueB === undefined) {
        return 1;
      } else if (valueA === undefined && typeof valueB === 'string') {
        return -1;
      }
      // If both are undefined, the loop will simply continue to the next key.
    }
    return 0; // If all keys are equal or missing, elements are considered equal
  });
  return sortedData;
};
